import React, {useEffect, useRef, useState, useSyncExternalStore, createContext, useId} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {history} from "../../history";
import {createDraftTracker, createDraftSaveGroup, draftSnapshot, dirtyStore} from "../../lib/unsavedChanges.mjs";

export const ParentDraftContext = createContext(null);
let askDiscard = null;
export function guardUnsavedChanges(action, ignoredLabels = []) {
  if (!dirtyStore.hasDirtyExcept(ignoredLabels)) {action();return;}
  if (askDiscard) askDiscard(action);
}
export function useUnsavedDraft(value, label, enabled = true, identity = "", saveWithParent = null) {
  const scope = useId();
  const ref = useRef(null);if (!ref.current) ref.current = {id: Symbol(label), tracker: createDraftTracker(), saveGroup: createDraftSaveGroup(), identity};
  const state = ref.current;
  if (state.identity !== identity) {state.tracker.reset();state.identity = identity;}
  const snapshot = draftSnapshot(value);const [, render] = useState(0);
  state.snapshot = snapshot;
  useEffect(() => {
    if (!saveWithParent) return undefined;
    const member = {
      checkpoint: () => ({identity, draft: state.tracker.checkpoint(state.snapshot)}),
      saved: checkpoint => {
        if (state.identity !== checkpoint.identity) return;
        state.tracker.saved(checkpoint.draft);
        render(n => n + 1);
      },
    };
    return saveWithParent.register(state.id, member);
  }, [saveWithParent, state, identity]);
  const dirty = enabled && state.tracker.dirty(snapshot);
  useEffect(() => {dirtyStore.set(state.id, label, dirty);}, [state, dirty, label]);
  useEffect(() => () => dirtyStore.remove(state.id), [state]);
  const touch = () => {if (enabled) {state.tracker.touch(snapshot);render(n => n + 1);}};
  // Capture the baseline without rendering: controlled checkboxes must finish
  // their native toggle before React/MUI reads checked in the change handler.
  const captureTouch = () => {if (enabled) state.tracker.touch(snapshot);};
  return {dirty,
    touch,
    capture: {"data-unsaved-scope": scope, onKeyDownCapture: event => {if (event.target.closest?.("[data-unsaved-scope]") === event.currentTarget && event.target.closest?.("input,textarea,select,[role=combobox]")) captureTouch();}, onChangeCapture: event => {if (event.target.closest?.("[data-unsaved-scope]") === event.currentTarget) captureTouch();}, onClickCapture: event => {
      if (event.target.closest?.("[data-unsaved-scope]") === event.currentTarget && event.target.closest?.("input,textarea,select,[role=combobox],[role=option],[role=checkbox],[role=radio],[role=switch],[data-draft-edit]")) captureTouch();
    }},
    guard: action => {if (dirty) guardUnsavedChanges(action);else action();},
    saveGroup: state.saveGroup,
    checkpoint: () => ({...state.tracker.checkpoint(snapshot), children: state.saveGroup.checkpoint()}),
    saved: checkpoint => {state.tracker.saved(checkpoint);state.saveGroup.saved(checkpoint?.children);render(n => n + 1);},
    reset: () => {state.tracker.reset();render(n => n + 1);},
  };
}
export function UnsavedNotice({dirty}) {
  return dirty ? <Alert severity="warning" role="status" sx={{mb: 2}}>Changes were made but have not been saved yet.</Alert> : null;
}
export function HeaderUnsavedNotice({compact = false}) {
  const dirty = useSyncExternalStore(dirtyStore.subscribe, dirtyStore.getSnapshot, () => "");
  return dirty ? <Alert severity="warning" role="status" sx={compact ? {borderRadius: 2, backgroundColor:"#fff2cf", color:"#704900", py:0, px:1.5, mr:"auto", maxWidth:"100%", minWidth:0, boxSizing:"border-box", "& .MuiAlert-message":{py:0.75,fontSize:"0.8rem",overflowWrap:"anywhere"}, "& .MuiAlert-icon":{py:0.75,fontSize:19}} : {borderRadius: 0}}>{compact ? `Unsaved changes · ${dirty}` : `Changes were made but have not been saved yet. (${dirty})`}</Alert> : null;
}
export function UnsavedChangesGuard() {
  const dirty = useSyncExternalStore(dirtyStore.subscribe, dirtyStore.getSnapshot, () => "");
  const [pending, setPending] = useState(null);
  useEffect(() => {askDiscard = action => setPending(() => action);return () => {askDiscard = null;};}, []);
  useEffect(() => {
    if (!dirty) return undefined;
    let unblock;let active = true;
    const block = transaction => setPending(() => () => {unblock();transaction.retry();queueMicrotask(() => {if (active && dirtyStore.getSnapshot()) unblock = history.block(block);});});
    unblock = history.block(block);
    return () => {active = false;unblock();};
  }, [Boolean(dirty)]);
  return <>
    <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
      <DialogTitle>Unsaved changes</DialogTitle><DialogContent>Changes were made but have not been saved yet. Leave without saving?</DialogContent>
      <DialogActions><Button onClick={() => setPending(null)}>Keep editing</Button><Button color="warning" onClick={() => {const action = pending;setPending(null);action?.();}}>Leave without saving</Button></DialogActions>
    </Dialog>
  </>;
}
